
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loader2 } from "lucide-react";
import NotFound from "./NotFound";
import { useIsMobile } from "@/hooks/use-mobile";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { usePublicProfile } from "@/hooks/usePublicProfile";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { ProfileContent } from "@/components/profile/ProfileContent";
import { useSubscriptionHandler } from "@/hooks/useSubscriptionHandler";

const PublicProfile = () => {
  const { username } = useParams();
  const isMobile = useIsMobile();

  if (!username) {
    return <NotFound />;
  }

  const normalizedUsername = username.replace(/\s+/g, '').toLowerCase();
  
  const { data: profile, isLoading: profileLoading } = usePublicProfile(username, normalizedUsername);
  
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error("Auth error:", error);
        return null;
      }
      return user;
    },
    retry: false,
  });

  const { data: stripeConfig } = useQuery({
    queryKey: ['stripe-config'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_secret', {
        secret_name: 'STRIPE_PUBLISHABLE_KEY'
      });
      
      if (error) throw error;
      return { publishableKey: data };
    }
  });
  
  const {
    data: subscription,
    isLoading: subscriptionLoading,
  } = useSubscriptionStatus(
    userData?.id ?? '', 
    profile?.id ?? ''
  );

  const { handleSubscribe, handleCancelSubscription, isLoading: subscribeLoading } = useSubscriptionHandler(profile);

  if (profileLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!profile) {
    return <NotFound />;
  }

  const totalLikes = profile.recipes?.reduce((acc, recipe) => acc + (recipe.likes_count || 0), 0) ?? 0;

  const handleCancelSubscriptionClick = async () => {
    if (profile?.id) {
      await handleCancelSubscription(profile.id);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <ProfileHeader
          username={profile.username}
          avatarUrl={profile.avatar_url}
          creatorCategory={profile.creator_category}
          totalRecipes={profile.recipes?.length ?? 0}
          totalLikes={totalLikes}
          lastRecipeDate={profile.recipes?.[0]?.created_at}
          subscriptionPrice={profile.subscription_price || 5}
          isSubscribed={!!subscription}
          currentUserId={userData?.id ?? ''}
          profileId={profile.id}
          onSubscribe={() => handleSubscribe('monthly')}
          loading={subscriptionLoading || subscribeLoading}
        />
        <ProfileContent
          profile={profile}
          isSubscribed={!!subscription}
          isMobile={isMobile}
          userLoading={userLoading}
          stripeConfig={stripeConfig}
          handleSubscribe={handleSubscribe}
          subscriptionType={subscription?.type}
          isCancelled={subscription?.cancelled}
          onCancelSubscription={handleCancelSubscriptionClick}
        />
      </main>
      <Footer />
    </div>
  );
};

export default PublicProfile;
